import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const { user, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  // Derive authenticated state directly from user data to avoid race conditions
  const isActuallyAuthenticated = !!user;

  useEffect(() => {
    // Wait for auth to finish loading
    if (isLoading) return;

    // If not authenticated, redirect to login
    if (!isActuallyAuthenticated) {
      setLocation('/login');
      return;
    }

    // If authenticated but not admin, redirect to dashboard
    if (user.role !== 'admin') {
      setLocation('/dashboard');
      return;
    }
  }, [user, isLoading, isActuallyAuthenticated, setLocation]);

  // Show nothing while loading
  if (isLoading) {
    return null;
  }

  // Show nothing if not authenticated or not admin (redirect will happen via useEffect)
  if (!isActuallyAuthenticated || user.role !== 'admin') {
    return null;
  }

  // Render children only if user is admin
  return <>{children}</>;
}
